import type { ProgramTimelineContentObject } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Delete2LineIcon, Modal, message } from 'ui';
import { useBookmarkObjectMutation, useUnbookmarkObjectMutation } from 'features/bookmark';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- The legacy program timeline is built on the feed queries
import {
  useDeleteActivityContentObjectMutation,
  useDeleteEventContentObjectMutation,
  useDeleteOrderPostContentObjectMutation,
  useDeletePollContentObjectMutation,
  useDeletePostContentObjectMutation,
} from 'features/feed/queries';
import { useReportContentObjectModalConfirm } from 'features/report';

/**
 * @deprecated Introduced as part of the #87353 feed refactor to manually patch the React Query cache
 * after each mutation. This ad-hoc cache-patching approach needs a better long-term solution.
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
export const useProgramTimelineActions = () => {
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();
  const { t } = useTranslation();

  const { mutateAsync: deleteActivity } = useDeleteActivityContentObjectMutation();
  const { mutateAsync: deleteEvent } = useDeleteEventContentObjectMutation();
  const { mutateAsync: deleteOrderPost } = useDeleteOrderPostContentObjectMutation();
  const { mutateAsync: deletePoll } = useDeletePollContentObjectMutation();
  const { mutateAsync: deletePost } = useDeletePostContentObjectMutation();
  const { isPending: isBookmarkPending, mutateAsync: bookmark } = useBookmarkObjectMutation();
  const { isPending: isUnbookmarkPending, mutateAsync: unbookmark } = useUnbookmarkObjectMutation();
  const { confirm: confirmReportObject, contextHolder: reportContextHolder } = useReportContentObjectModalConfirm();

  const deleteCurriculumObject = useCallback(
    ({ object: contentObject }: { object: ProgramTimelineContentObject }) => {
      const contentObjectTypeLabel = {
        activity: 'activity',
        event: 'event',
        poll: 'poll',
        post: 'post',
        // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: fix later
        withings_order_post: 'post',
      }[contentObject.type];

      modal.confirm({
        content: 'This action is irreversible.',
        icon: <Delete2LineIcon type="error" />,
        okText: 'Delete',
        async onOk() {
          switch (contentObject.type) {
            case 'activity':
              await deleteActivity({ contentObject });
              break;

            case 'event':
              await deleteEvent({ contentObject });
              break;

            case 'poll':
              await deletePoll({ contentObject });
              break;

            case 'post':
              await deletePost({ contentObject });
              break;

            case 'withings_order_post':
              await deleteOrderPost({ contentObject });
              break;
          }
        },
        title: `Are you sure you want to delete this ${contentObjectTypeLabel}?`,
        type: 'error',
      });
    },
    [deleteActivity, deleteEvent, deleteOrderPost, deletePoll, deletePost, modal],
  );

  const reportObject = useCallback(
    (object: ProgramTimelineContentObject) => {
      const contentObjectTypeLabel = {
        activity: 'activity',
        event: 'event',
        poll: 'poll',
        post: 'post',
        // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: fix later
        withings_order_post: 'post',
      }[object.type];

      confirmReportObject({
        id: object.id,
        onSuccess() {
          message.success('Successfully reported.');
        },
        title: t(`Are you sure you want to report this ${contentObjectTypeLabel}?`),
      });
    },
    [confirmReportObject, t],
  );

  const bookmarkObject = useCallback(
    async (object: ProgramTimelineContentObject) => {
      const parameters = { contentObject: object, type: 'rails' as const };

      await (object.bookmarked ? unbookmark(parameters) : bookmark(parameters));
    },
    [bookmark, unbookmark],
  );

  const requestUserProfile = useCallback(async (user: { id: string }) => navigate(`/members/${user.id}`), [navigate]);

  return useMemo(
    () => ({
      bookmarkObject,
      contextHolder: (
        <>
          {contextHolder}
          {reportContextHolder}
        </>
      ),
      deleteCurriculumObject,
      isBookmarking: isBookmarkPending || isUnbookmarkPending,
      reportObject,
      requestUserProfile,
    }),
    [
      bookmarkObject,
      contextHolder,
      deleteCurriculumObject,
      isBookmarkPending,
      isUnbookmarkPending,
      reportContextHolder,
      reportObject,
      requestUserProfile,
    ],
  );
};
